import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { MdCheck, MdLocalShipping, MdReceipt, MdArrowForward, MdPrint } from 'react-icons/md';
import Breadcrumb from '../components/shared/Breadcrumb';
import Carousel from '../components/shared/Carousel';
import PopularCategories from '../components/shared/PopularCategories';
import image61 from '../assets/image61.png';
import image62 from '../assets/image62.png';
import image63 from '../assets/image63.png';
import '../styles/OrderConfirmationPage.css';

interface OrderItem {
  name: string;
  qty: number;
  price: number;
  image: string;
}

interface OrderData {
  orderNumber: string;
  items: OrderItem[];
  subtotal: number;
  shipping: number;
  tax: number;
  total: number;
  paymentMethod: string;
  address: {
    name: string;
    street: string;
    street2: string;
    city: string;
    state: string;
    zip: string;
    country: string;
    phone: string;
    email: string;
  };
  date: string;
}

const PROMO_SLIDES = [
  { id: 1, image: image62, tag: 'WHOLESALE DEALS', title: 'Future-Proof Your Inventory', subtitle: 'Explore premium selections and lock in wholesale pricing today.', cta: { label: 'Shop Now' }, bgColor: '#1A1040' },
  { id: 2, image: image61, tag: 'LIMITED TIME', title: 'Beverages at Bulk Prices', subtitle: 'Stock up on trending drinks with exclusive partner discounts.', cta: { label: 'View Deals' }, bgColor: '#2D1A0E' },
  { id: 3, image: image63, tag: 'ECO-FRIENDLY', title: 'Natural & Organic Range', subtitle: 'Sustainable products your customers love — delivered fast.', cta: { label: 'Explore Now' }, bgColor: '#0A2E1A' },
];

const OrderConfirmationPage: React.FC = () => {
  const navigate = useNavigate();
  const [order, setOrder] = useState<OrderData | null>(null);
  const [animReady, setAnimReady] = useState(false);
  const [confettiKey, setConfettiKey] = useState(0);

  useEffect(() => {
    const data = sessionStorage.getItem('lastOrder');
    if (data) {
      setOrder(JSON.parse(data));
      setTimeout(() => setAnimReady(true), 100);
    } else {
      navigate('/products');
    }
  }, [navigate]);

  // Re-trigger confetti every 5 seconds
  useEffect(() => {
    if (!animReady) return;
    const interval = setInterval(() => {
      setConfettiKey((k) => k + 1);
    }, 5000);
    return () => clearInterval(interval);
  }, [animReady]);

  if (!order) return null;

  const estimatedDelivery = new Date();
  estimatedDelivery.setDate(estimatedDelivery.getDate() + 5);
  const deliveryDate = estimatedDelivery.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

  return (
    <div className="order-conf">
      <Breadcrumb extra="Order Confirmation" />

      {/* ── Section 1: Success Hero ── */}
      <section className="order-conf__hero">
        <div className="order-conf__hero-inner">
          {/* Animated checkmark */}
          <div className={`order-conf__check ${animReady ? 'order-conf__check--visible' : ''}`}>
            <div className="order-conf__check-circle">
              <svg className="order-conf__check-svg" viewBox="0 0 52 52">
                <circle className="order-conf__check-ring" cx="26" cy="26" r="24" />
                <path className="order-conf__check-path" d="M14 27l7 7 16-16" />
              </svg>
            </div>
          </div>

          <h1 className={`order-conf__title ${animReady ? 'order-conf__title--visible' : ''}`}>
            Order Placed Successfully!
          </h1>
          <p className={`order-conf__subtitle ${animReady ? 'order-conf__subtitle--visible' : ''}`}>
            Thank you for your order. We've sent a confirmation email to <strong>{order.address.email}</strong>
          </p>

          <div className={`order-conf__order-id ${animReady ? 'order-conf__order-id--visible' : ''}`}>
            <MdReceipt />
            <span>Order #{order.orderNumber}</span>
          </div>

          {/* Confetti particles — re-triggers via key */}
          {animReady && (
            <div className="order-conf__confetti-container" key={confettiKey}>
              {Array.from({ length: 40 }).map((_, i) => (
                <span
                  key={i}
                  className="order-conf__confetti-particle"
                  style={{
                    '--cx': `${(Math.random() - 0.5) * 500}px`,
                    '--cy': `${-(Math.random() * 300 + 100)}px`,
                    '--cr': `${Math.random() * 720}deg`,
                    '--cd': `${Math.random() * 0.5}s`,
                    background: ['#10B981', '#34D399', '#FBBF24', '#3B82F6', '#EF4444', '#8B5CF6', '#EC4899', '#14B8A6'][i % 8],
                    width: `${6 + Math.random() * 6}px`,
                    height: `${4 + Math.random() * 4}px`,
                    borderRadius: i % 3 === 0 ? '50%' : '1px',
                  } as React.CSSProperties}
                />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* ── Section 2: Order Details ── */}
      <section className="order-conf__details">
        <div className="order-conf__details-grid">

          {/* Left: Order Items */}
          <div className="order-conf__card order-conf__card--items">
            <h2 className="order-conf__card-title">Order Items</h2>
            <div className="order-conf__items">
              {order.items.map((item, i) => (
                <div key={i} className="order-conf__item">
                  <img src={item.image} alt={item.name} className="order-conf__item-img" />
                  <div className="order-conf__item-info">
                    <span className="order-conf__item-name">{item.name}</span>
                    <span className="order-conf__item-qty">Qty: {item.qty}</span>
                  </div>
                  <span className="order-conf__item-price">${(item.price * item.qty).toFixed(2)}</span>
                </div>
              ))}
            </div>
            <div className="order-conf__totals">
              <div className="order-conf__total-row">
                <span>Subtotal</span>
                <span>${order.subtotal.toFixed(2)}</span>
              </div>
              <div className="order-conf__total-row">
                <span>Shipping</span>
                <span className={order.shipping === 0 ? 'order-conf__free' : ''}>{order.shipping === 0 ? 'Free' : `$${order.shipping.toFixed(2)}`}</span>
              </div>
              <div className="order-conf__total-row">
                <span>Tax</span>
                <span>${order.tax.toFixed(2)}</span>
              </div>
              <div className="order-conf__total-row order-conf__total-row--final">
                <span>Total</span>
                <span>${order.total.toFixed(2)}</span>
              </div>
            </div>
          </div>

          {/* Right: Shipping + Payment info */}
          <div className="order-conf__sidebar">
            {/* Shipping */}
            <div className="order-conf__card">
              <h2 className="order-conf__card-title">
                <MdLocalShipping className="order-conf__card-icon" /> Shipping Details
              </h2>
              <div className="order-conf__info-rows">
                <p className="order-conf__info-name">{order.address.name}</p>
                <p>{order.address.street}</p>
                {order.address.street2 && <p>{order.address.street2}</p>}
                <p>{order.address.city}, {order.address.state} {order.address.zip}</p>
                <p>{order.address.country}</p>
                <p className="order-conf__info-contact">{order.address.phone}</p>
                <p className="order-conf__info-contact">{order.address.email}</p>
              </div>
            </div>

            {/* Payment */}
            <div className="order-conf__card">
              <h2 className="order-conf__card-title">
                <MdReceipt className="order-conf__card-icon" /> Payment & Delivery
              </h2>
              <div className="order-conf__info-rows">
                <div className="order-conf__info-pair">
                  <span className="order-conf__info-label">Payment Method</span>
                  <span className="order-conf__info-value">{order.paymentMethod}</span>
                </div>
                <div className="order-conf__info-pair">
                  <span className="order-conf__info-label">Order Date</span>
                  <span className="order-conf__info-value">{order.date}</span>
                </div>
                <div className="order-conf__info-pair">
                  <span className="order-conf__info-label">Est. Delivery</span>
                  <span className="order-conf__info-value order-conf__info-value--green">{deliveryDate}</span>
                </div>
                <div className="order-conf__info-pair">
                  <span className="order-conf__info-label">Status</span>
                  <span className="order-conf__status-badge">Processing</span>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="order-conf__actions">
              <button className="order-conf__btn order-conf__btn--primary" onClick={() => navigate('/products')}>
                Continue Shopping <MdArrowForward />
              </button>
              <button className="order-conf__btn order-conf__btn--outline" onClick={() => window.print()}>
                <MdPrint /> Print Receipt
              </button>
            </div>
          </div>

        </div>
      </section>

      {/* ── Section 3: Timeline ── */}
      <section className="order-conf__timeline-section">
        <div className="order-conf__timeline-card">
          <h2 className="order-conf__card-title">Order Timeline</h2>
          <div className="order-conf__timeline">
            <div className="order-conf__timeline-step order-conf__timeline-step--done">
              <div className="order-conf__timeline-dot"><MdCheck /></div>
              <div className="order-conf__timeline-text">
                <span className="order-conf__timeline-label">Order Placed</span>
                <span className="order-conf__timeline-date">{order.date}</span>
              </div>
            </div>
            <div className="order-conf__timeline-step order-conf__timeline-step--active">
              <div className="order-conf__timeline-dot"><MdCheck /></div>
              <div className="order-conf__timeline-text">
                <span className="order-conf__timeline-label">Processing</span>
                <span className="order-conf__timeline-date">In progress</span>
              </div>
            </div>
            <div className="order-conf__timeline-step">
              <div className="order-conf__timeline-dot" />
              <div className="order-conf__timeline-text">
                <span className="order-conf__timeline-label">Shipped</span>
                <span className="order-conf__timeline-date">Pending</span>
              </div>
            </div>
            <div className="order-conf__timeline-step">
              <div className="order-conf__timeline-dot" />
              <div className="order-conf__timeline-text">
                <span className="order-conf__timeline-label">Delivered</span>
                <span className="order-conf__timeline-date">Est. {deliveryDate}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Carousel ── */}
      <section className="order-conf__carousel">
        <Carousel slides={PROMO_SLIDES} autoPlay interval={5000} />
      </section>

      {/* ── Popular Categories ── */}
      <PopularCategories />
    </div>
  );
};

export default OrderConfirmationPage;
